"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useOutsideClick } from "@packages/hooks/DetectOutsideClick";
import Form from "next/form";
import { fetchAuthSession } from "@aws-amplify/auth";
import {
  GA4DataLayerFn,
  getCookie,
  getQualTextForQualIdClient,
} from "@packages/lib/utlils/helper-function";
import { useRouter } from "next/navigation";
import {
  SearchSubjectItem,
  SearchLocationItem,
} from "../search-utils/search.model";
import { sanitizeSearchKey } from "../search-utils/search-utils";
import ButtonLoader from "../../cards/interaction-button/ButtonLoader";
import emitter, { CustomEvents } from "@packages/lib/eventEmitter/eventEmitter";
import { DataLayerGA4AttrType } from "@packages/lib/types/datalayerGA";
import { usePathname } from "next/navigation";
import { parseUCASCookie } from "@packages/lib/utlils/getUcasvalue";
import { sanitizeInput } from "@packages/lib/utlils/sanitization";
interface ClearingTabProps {
  customGA?: DataLayerGA4AttrType;
  searchFormHandle?: any;
  setsearchFormHandle?: any;
  placeholder?: any;
  showLocation?: boolean;
  showUcas?: boolean;
  clearingCourseList: SearchSubjectItem[];
  locationList: SearchLocationItem[];
}

const ClearingSearch = ({
  customGA,
  placeholder,
  clearingCourseList: courseList,
  locationList,
  showLocation = true,
  showUcas = true,
}: ClearingTabProps) => {
  const [searchState, setSearchState] = useState<{
    subject: any;
    location: any;
    ucasScore?: number;
  }>({ subject: {}, location: {} });
  const [activeDropdown, setActiveDropdown] = useState<
    "subject" | "location" | null
  >(null);
  const pathName = usePathname();
  const [dropdownIndex, setdropdownIndex] = useState<number>(0);
  const [filteredCourseList, setFilteredCourseList] = useState<any[]>([
    ...(courseList?.length ? courseList : []),
  ]);
  const [subSearchIsValid, setSubSearchIsValid] = useState<boolean>(false);
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  let ucasVal: any = 0;

  useEffect(() => {
    function getUcasFromCalculator() {
      const ucasScore = parseUCASCookie();
      handleUcasChange({ target: { value: ucasScore } });
    }
    emitter.on(CustomEvents?.UcasUpdates, getUcasFromCalculator);
    return () => {
      emitter.off(CustomEvents?.UcasUpdates, getUcasFromCalculator);
    };
  }, []);

  useEffect(() => {
    const searchText =
      searchState.subject?.description?.toLowerCase()?.trim() || "";
    if (!searchText) return;

    const prioritySearch = (
      list: { description: string; [key: string]: any }[],
      searchText: string
    ): { description: string; [key: string]: any }[] => {
      if (!searchText) return list;
      return list
        ?.map((item) => ({
          ...item,
          position: item?.description?.toLowerCase().indexOf(searchText),
          startsWithSearch: item?.description
            ?.toLowerCase()
            ?.startsWith(searchText),
          exactMatch: item?.description?.toLowerCase() === searchText,
        }))
        .filter((item) => item.position !== -1)
        .sort((a, b) => {
          if (a.exactMatch !== b.exactMatch) return a.exactMatch ? -1 : 1;
          if (a.startsWithSearch !== b.startsWithSearch)
            return a.startsWithSearch ? -1 : 1;
          if (a.position !== b.position) return a.position - b.position;
          return a?.description?.localeCompare(b?.description);
        })
        ?.map((item: any) => ({
          description: item?.description,
          url: item.url,
          category_code: item.category_code,
          browse_cat_id: item.browse_cat_id,
          parent_subject: item.parentSubject,
          qual_Code: item?.qualCode,
        }));
    };

    if (searchState.subject.description?.length > 2)
      setFilteredCourseList(prioritySearch(courseList, searchText));
    else setFilteredCourseList(courseList);
  }, [searchState.subject.description, searchState.location.description]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(containerRef, resetOpenedDropdown);

  function enableButtonLoader() {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 8000);
  }

  function isValidInternalUrl(url: string): boolean {
    try {
      // Only allow relative URLs or same-origin URLs
      if (url.startsWith('/')) {
        return true; // Relative URLs are safe
      }
      const urlObj = new URL(url);
      const allowedDomains = [
        'whatuni.com',
        'www.whatuni.com',
        'localhost',
        window.location.hostname
      ];
      // Only allow HTTPS for external domains
      if (urlObj.protocol !== 'https:' && urlObj.hostname !== 'localhost') {
        return false;
      }
      return allowedDomains.includes(urlObj.hostname) || urlObj.hostname.endsWith('.whatuni.com');
    } catch {
      return false;
    }
  }

  function navigateTo(newUrl: string) {
    if (!isValidInternalUrl(newUrl)) {
      console.warn('Invalid URL detected, navigation blocked');
      return;
    }
    enableButtonLoader();
    window.location.href = newUrl;
  }

  function resetOpenedDropdown() {
    setdropdownIndex(0);
    setActiveDropdown(() => null);
  }

  function getUcasQueryParams() {
    return ucasVal || searchState.ucasScore
      ? `&score=${searchState.ucasScore || ucasVal}`
      : "";
  }

  const courseSearchGALog = () => {
    GA4DataLayerFn({
      cpe_parent_subject:
        searchState?.subject?.parent_subject ??
        searchState?.subject?.description,
      cpe_child_subject: searchState?.subject?.parent_subject
        ? searchState?.subject?.description
        : "NA",
      study_level: "undergraduate",
      page_name: localStorage?.getItem("gaPageName") || "",
      ucas_points: searchState?.ucasScore?.toString() || "NA",
      ...customGA,
    });
  };

  const searchHandler = async () => {
    if (
      !searchState?.subject?.regionUrl &&
      !searchState.subject.url &&
      !searchState?.subject?.description?.trim()
    ) {
      setSubSearchIsValid(true);
      return;
    }
    courseSearchGALog();
    setIsLoading(true);
  };

  useEffect(() => {
    const proceedClearingSearch = async () => {
      async function getMinAndUcasValue() {
        let ucasVal: any = 0;
        try {
          await fetchAuthSession({ forceRefresh: true });
          ucasVal = parseUCASCookie();
          // const minval: any = getCookie("min");
          // min = parseInt(minval, 10);
        } catch (error) {
          // const minval: any = getCookie("min");
          // min = parseInt(minval, 10);
          const cookiesval1: any = decodeURIComponent(
            getCookie("UCAS") || "{}"
          );
          const point: any = JSON.parse(cookiesval1);
          ucasVal = point?.ucasPoint;
        }
        return [ucasVal];
      }

      if (
        !searchState?.subject?.regionUrl &&
        !searchState.subject.url &&
        !searchState?.subject?.description?.trim()
      ) {
        setSubSearchIsValid(true);
      }

      [ucasVal] = await getMinAndUcasValue();
      const domain =
        process.env.NEXT_PUBLIC_ENVIRONMENT === "prd"
          ? "https://www.whatuni.com"
          : "";
      if (searchState?.subject?.url && searchState?.location?.regionUrl) {
        const newUrl = `${domain}${searchState.subject.url}&location=${searchState?.location?.regionUrl}${getUcasQueryParams()}`;
        navigateTo(newUrl);
      } else if (searchState.subject?.url) {
        const newUrl = `${domain}${searchState.subject.url}${getUcasQueryParams()}`;
        navigateTo(newUrl);
      } else if (searchState?.subject?.description?.trim()) {
        keywordSearch(true);
      }
    };
    if (isLoading) {
      proceedClearingSearch();
    }
  }, [isLoading]);

  useEffect(() => {
    const bodyContainer: any = document.getElementById("bodyContainer");
    const footer: any = document.getElementById("footerComponent");
    if (!bodyContainer) {
      return;
    }
    if (isLoading && !pathName?.includes("courses/search")) {
      emitter.emit("searchpageloading", true);
      bodyContainer?.classList?.add("hidden");
      footer?.classList?.add("hidden");
    } else {
      emitter.emit("searchpageloading", false);
      bodyContainer?.classList?.remove("hidden");
      footer?.classList?.remove("hidden");
    }
  }, [isLoading]);

  const keywordSearch = async (canMatch: any) => {
    setIsLoading(true);
    const matchedSubject = filteredCourseList?.find(
      (item: any) =>
        item?.description?.toLowerCase() ===
        searchState?.subject?.description?.trim()?.toLowerCase()
    );
    const domain =
      process.env.NEXT_PUBLIC_ENVIRONMENT === "prd"
        ? "https://www.whatuni.com"
        : "";

    if (canMatch && matchedSubject && searchState.location?.regionUrl) {
      const sanitizedRegionName = sanitizeSearchKey(
        searchState?.location?.regionUrl
      );
      const newUrl = `${domain}${matchedSubject.url}&location=${sanitizedRegionName}${getUcasQueryParams()}`;
      navigateTo(newUrl);
    } else if (canMatch && matchedSubject) {
      const newUrl = `${domain}${matchedSubject.url}${getUcasQueryParams()}`;
      navigateTo(newUrl);
    } else {
      const sanitizedDescription = sanitizeSearchKey(
        searchState?.subject?.description
      );
      navigateTo(
        `${domain}/degree-courses/search?clearing&q=${sanitizedDescription}`
      );
    }
  };

  const trimSearchText = (searchText: string) => {
    return searchText?.replace(/\s{2,}/g, " ");
  };

  const handleDropdownArrowFunctionality = (e: any) => {
    if (!activeDropdown) return;
    const allOptions: any =
      (activeDropdown === "subject" ? filteredCourseList : locationList) || [];
    const currentIndex = dropdownIndex;
    let newIndex = currentIndex;
    let newElement: HTMLElement | null = null;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        newIndex = currentIndex < allOptions.length - 1 ? currentIndex + 1 : 0;
        setdropdownIndex(newIndex);
        newElement = document.querySelector(`[data-index="${newIndex}"]`);
        break;

      case "ArrowUp":
        e.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : allOptions.length - 1;
        setdropdownIndex(newIndex);
        newElement = document.querySelector(`[data-index="${newIndex}"]`);
        break;

      case "Enter":
        e.preventDefault();
        if (dropdownIndex) {
          setSearchState((prev) => ({
            ...prev,
            [activeDropdown]: allOptions[dropdownIndex - 1],
          }));
          setActiveDropdown(() => null);
        } else searchHandler();
        break;
    }
    document
      .querySelector(`[data-index="${currentIndex}"]`)
      ?.classList.remove("bg-blue-50", "underline");
    newElement?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    newElement?.classList.add("bg-blue-50", "underline");
  };

  const handleUcasChange = (e: any) => {
    const ucasScore = e.target.value;
    const regex = /^[0-9\b]+$/;
    if (ucasScore === "" || regex.test(ucasScore))
      setSearchState((prev) => ({ ...prev, ucasScore }));
  };

  return (
    <div className="flex flex-col gap-[24px] min-h-[60px]">
      <div className="bg-white rounded-[32px] p-[16px] border border-neutral300 hover:border-primary-500 shadow-custom-1 md:pl-[24px] md:p-[10px]">
        <Form action={searchHandler}>
          <div
            ref={containerRef}
            className="flex flex-col items-stretch md:flex-row md:items-center lg:gap-[4px]"
          >
            {/* Subject */}
            <div className="w-full relative border-grey-200 grow md:border-l-0">
              <div className="flex items-center mt-[-4px] mb-[12px] md:my-[0]">
                <input
                  type="text"
                  data-text-id="clearing_subject_input"
                  value={searchState.subject?.description || ""}
                  className="form-control w-full focus:outline-none small text-black placeholder:text-gray-500 px-[0] py-[10px] md:px-[16px] md:pl-[0]"
                  aria-label="submenu"
                  placeholder={placeholder || "Enter subject"}
                  onChange={(event) => {
                    setSearchState((prev) => ({
                      ...prev,
                      subject: {
                        description: trimSearchText(event.target.value),
                      },
                    }));
                    setActiveDropdown(() => "subject");
                  }}
                  onClick={() => {
                    setdropdownIndex(() => 0);
                    setActiveDropdown(() => "subject");
                  }}
                  onKeyDown={handleDropdownArrowFunctionality}
                />
              </div>
              {activeDropdown === "subject" &&
                searchState.subject.description?.length > 2 && (
                  <div className="w-full z-[1] bg-white shadow-custom-3 rounded-[4px] absolute left-0 top-[54px] max-h-[311px] overflow-y-scroll custom-scrollbar-2 overflow-hidden">
                    <div
                      className="px-[16px] py-[12px]"
                      onClick={() => {
                        courseSearchGALog();
                        resetOpenedDropdown();
                        keywordSearch(false);
                      }}
                    >
                      <p className="x-small font-semibold text-black tracking-[1px] leading-[18px]">
                        KEYWORD SEARCH FOR
                      </p>
                      <p className="small text-primary-400">
                        {searchState?.subject?.description}
                      </p>
                    </div>
                    <ul>
                      {filteredCourseList?.map((subject, idx: number) => (
                        <li
                          key={"subject_" + idx}
                          data-index={idx + 1}
                          className="block small px-[16px] py-[12px] hover:bg-blue-50 hover:underline"
                          onClick={() => {
                            setSearchState((prev) => ({
                              ...prev,
                              subject: subject,
                            }));
                            resetOpenedDropdown();
                          }}
                        >
                          {subject?.description}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
            {/* Subject */}

            {/* Location */}
            {showLocation && (
              <div className="w-full relative grow border-y-[1px] md:border-l md:border-y-0">
                <div className="flex items-center my-[12px] md:my-[0]">
                  <input
                    type="text"
                    data-text-id="clearing_location_input"
                    className="form-control w-full focus:outline-none small text-black placeholder:text-gray-500 px-[0] py-[10px] md:px-[16px]"
                    aria-label="submenu"
                    placeholder="Location (optional)"
                    value={searchState.location?.regionName || ""}
                    onKeyDown={handleDropdownArrowFunctionality}
                    onChange={(event) => {
                      setSearchState((prev) => ({
                        ...prev,
                        location: {
                          regionName: trimSearchText(event.target.value),
                        },
                      }));
                    }}
                    onClick={() => {
                      setdropdownIndex(0);
                      setActiveDropdown(() => "location");
                    }}
                    readOnly
                  />
                </div>
                {activeDropdown === "location" && (
                  <div className="w-full z-[1] bg-white shadow-custom-3 rounded-[4px] absolute left-0 top-[54px] w-full max-h-[310px] overflow-y-auto custom-scrollbar-2">
                    <ul>
                      {locationList?.map((location, idx: number) => (
                        <li
                          key={"location_" + idx}
                          data-index={idx + 1}
                          className="block small px-[16px] py-[12px] hover:bg-blue-50 hover:underline"
                          onClick={() => {
                            setSearchState((prev) => ({
                              ...prev,
                              location: location,
                            }));
                            resetOpenedDropdown();
                          }}
                        >
                          {location?.regionName}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {/* Location */}

            {/* UCAS */}
            {showUcas && (
              <div className="w-full relative grow md:border-l border-neutral200">
                <div className="flex items-center my-[12px] md:my-[0]">
                  <input
                    type="text"
                    data-text-id="clearing_ucas_input"
                    className="form-control w-full focus:outline-none small text-black placeholder:text-gray-500 px-[0] py-[10px] md:px-[16px]"
                    aria-label="submenu"
                    placeholder="UCAS points (optional)"
                    value={searchState.ucasScore || ""}
                    onChange={handleUcasChange}
                    onClick={() => {
                      setdropdownIndex(0);
                      setActiveDropdown(() => null);
                    }}
                  />
                  {searchState?.ucasScore && (
                    <span
                      id="ucas-suffix"
                      className="absolute text-500 text-sm pointer-events-none top-1/2 right-[16px] -translate-y-1/2 truncate"
                      style={{
                        left: `${(searchState.ucasScore.toString().length || 1) * 8 + 18}px`,
                      }} // assuming ~8px per character
                    >
                      UCAS points
                    </span>
                  )}
                </div>
              </div>
            )}
            {/* UCAS */}

            {/* Search CTA */}
            <div className="pt-[2px] md:pt-[0]">
              <button
                type="submit"
                data-text-id="clearing_search_button"
                className="btn btn-primary w-full flex items-center justify-center gap-[6px] px-[24px] py-[10px] md:w-[114px]"
              >
                <ButtonLoader isLoading={isLoading}>
                  <Image
                    src="/static/assets/icons/search_icon.svg"
                    width="18"
                    height="18"
                    alt="Search icon"
                  />
                  Search
                </ButtonLoader>
              </button>
            </div>
            {/* Search CTA */}
          </div>
        </Form>
      </div>
      {subSearchIsValid && (
        <p className="small text-negative-default"> Please enter subject</p>
      )}
    </div>
  );
};

export default ClearingSearch;
